import React, { FC } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { SemibigHeader } from '../../../ui/Header/Header';
import {
  DisplayDateRange,
  GetTranslationsQueryVariables,
  Query,
  NarvarStatusCode,
  QueryFormatEddArgs,
  FormatBlockEddQuery,
} from '../../../../../../__generated__/loma';
import { renderTemplate } from '../utils';
import { Clock } from './Icons';
import { useTheme } from '../../../../hooks/useTheme';
import translationsQueryByKey from '../../../../../../graphql/translationsQueryByKey';
import getFormattedEdd from '../../../../../../graphql/getFormattedEdd';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import styles from './Status.module.css';

interface StatusProps {
  displayDate: string;
  displayDateRange?: DisplayDateRange | null;
  status?: string;
  statusCode?: NarvarStatusCode;
}

const Status: FC<StatusProps> = (props) => {
  const { displayDate, displayDateRange, status, statusCode } = props;

  const { state } = usePageContext();
  const {
    retailerMoniker,
    localeLanguage,
    localeCountry,
    versionName,
    type,
    cache,
  } = state;

  const stringifiedKeys = ['blockDeliveryStatusEtaText', 'trackingStatus'].join(
    '\n',
  );

  const { data: translationsData } = useSuspenseQuery<
    Pick<Query, 'retailerTranslations'>,
    GetTranslationsQueryVariables
  >(translationsQueryByKey(stringifiedKeys), {
    skip: !statusCode && !status,
    variables: {
      retailerMoniker,
      localeLanguage: localeLanguage || 'en',
      localeCountry,
      versionName,
      type,
      category: statusCode,
      status,
      cache,
    },
  });

  const { blockDeliveryStatusEtaText, trackingStatus } =
    translationsData?.retailerTranslations ?? {};

  const { bodytextDefaultNone } = useTheme();
  const eddFormatTranslationKeys: QueryFormatEddArgs['eddCombos'][0]['eddFormatTranslationKeys'] =
    [
      {
        sameDay: 'blockDeliveryStatusEtaFormattedTime',
        lastDay: 'blockDeliveryStatusEtaFormattedTime',
        nextDay: 'blockDeliveryStatusEtaFormattedTime',
        lastWeek: 'blockDeliveryStatusEtaFormattedTime',
        nextWeek: 'blockDeliveryStatusEtaFormattedTime',
        sameElse: 'blockDeliveryStatusEtaFormattedTime',
      },
    ];

  const { data } = useSuspenseQuery<FormatBlockEddQuery, QueryFormatEddArgs>(
    getFormattedEdd,
    {
      variables: {
        retailerMoniker,
        versionName,
        localeLanguage,
        localeCountry,
        eddCombos: [
          { displayDate, eddFormatTranslationKeys, dateOnly: false },
          {
            displayDate: displayDateRange?.begin || '',
            eddFormatTranslationKeys,
            dateOnly: false,
          },
          {
            displayDate: displayDateRange?.end || '',
            eddFormatTranslationKeys,
            dateOnly: false,
          },
        ],
      },
    },
  );

  const { formatEdd } = data ?? {};

  const [
    displayDateFormatted,
    displayDateRangeBeginFormatted,
    displayDateRangeEndFormatted,
  ] = formatEdd.formattedEdds;

  const trackingStatusText = renderTemplate(trackingStatus ?? '', {
    arrival: { minutes: '28' },
  });

  const eta = renderTemplate(blockDeliveryStatusEtaText ?? '', {
    arrival: {
      begin: displayDateRangeBeginFormatted[0],
      end: displayDateRangeEndFormatted[0],
      time: displayDateFormatted[0],
    },
    delivery: {
      time: displayDateFormatted[0],
    },
    pickup: {
      begin: displayDateRangeBeginFormatted[0],
      end: displayDateRangeEndFormatted[0],
      time: displayDateFormatted[0],
    },
  });

  const showETA =
    displayDateFormatted[0] ||
    displayDateRangeBeginFormatted[0] ||
    displayDateRangeEndFormatted[0];

  return (
    <div>
      <SemibigHeader as="h2">{trackingStatusText}</SemibigHeader>
      {showETA && (
        <div className={styles.eddContainer}>
          <div className={styles.clockIcon}>
            <Clock />
          </div>
          <div className={styles.statusText}>
            <span style={{ ...bodytextDefaultNone }}>{eta}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Status;
